import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiX, FiEye, FiEyeOff, FiLock } from 'react-icons/fi';
import { getFormErrorMessage } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { Modal } from '../../design-system';

const ChangePasswordDialog = ({ user, onSave, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const watchPassword = watch('password');

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      await onSave(data.password);
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="md"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100">
              <FiLock className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Cambiar Contraseña
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Cambiar contraseña para: <span className="font-medium text-gray-900">{user.name}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            No se requiere la contraseña anterior
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="change-password-form">
              {/* Password */}
              <div>
                <label htmlFor="password" className="form-label">
                  Nueva contraseña *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`form-input pr-10 ${errors.password ? 'border-danger-500' : ''}`}
                    placeholder="Contraseña"
                    {...register('password', {
                      required: true,
                      minLength: 8,
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FiEyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <FiEye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.password.type === 'minLength'
                      ? 'La contraseña debe tener al menos 8 caracteres'
                      : getFormErrorMessage(errors, 'password')
                    }
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              {watchPassword && (
                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirmar contraseña *
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className={`form-input ${errors.confirmPassword ? 'border-danger-500' : ''}`}
                    placeholder="Confirmar contraseña"
                    {...register('confirmPassword', {
                      required: !!watchPassword,
                      validate: value => value === watchPassword || 'Las contraseñas no coinciden',
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-danger-600">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              )}

        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="change-password-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Cambiando...</span>
            </div>
          ) : (
            'Cambiar Contraseña'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePasswordDialog;
